import React, { useState } from "react";
import { Pane, TextInput, Select, Button, toaster, FilePicker } from "evergreen-ui";
import "./AdminCarHire.css";

const AdminCarHire = () => {
  const [form, setForm] = useState({
    car_name: "",
    price: "",
    image: null,
    seats: "",
    bags: "",
    transmission: "automatic",
    fuel: "petrol",
    air_conditioning: true,
    airbags: "",
    reverse_camera: false,
    lane_assist: false,
    gps: false,
    usb: false,
    bluetooth: false,
    touchscreen: false,
    mileage: "",
  });

  const handleFileChange = (files) => {
    if (files && files[0]) {
      setForm({ ...form, image: files[0] });
    }
  };

  const handleSubmit = async () => {
    const formData = new FormData();

    // Append all fields to FormData without validation or conversion
    Object.keys(form).forEach((key) => {
      if (key === "image" && form[key]) {
        formData.append(key, form[key]);
      } else if (form[key] !== null) {
        formData.append(key, form[key]);
      }
    });

    // Debug: Log FormData contents
    for (let pair of formData.entries()) {
      console.log(`${pair[0]}: ${pair[1]}`);
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/carhires`, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to save car");
      }

      toaster.success("Car added successfully!");
      setForm({
        car_name: "",
        price: "",
        image: null,
        seats: "",
        bags: "",
        transmission: "automatic",
        fuel: "petrol",
        air_conditioning: true,
        airbags: "",
        reverse_camera: false,
        lane_assist: false,
        gps: false,
        usb: false,
        bluetooth: false,
        touchscreen: false,
        mileage: "",
      });
    } catch (error) {
      toaster.danger(error.message);
    }
  };

  return (
    <Pane className="form-container">
      <h2>Car Hire Management</h2>
      <Pane className="form-group">
        <Pane className="input-row">
          <span>Car Name</span>
          <TextInput
            value={form.car_name}
            onChange={(e) => setForm({ ...form, car_name: e.target.value })}
            placeholder="Enter car name"
          />
        </Pane>

        <Pane className="input-row">
          <span>Price</span>
          <TextInput
            value={form.price}
            onChange={(e) => setForm({ ...form, price: e.target.value })}
            type="number"
            step="0.01"
            placeholder="Enter price"
          />
        </Pane>

        <Pane className="input-row">
          <span>Image</span>
          <FilePicker
            width={250}
            onChange={handleFileChange}
            placeholder="Select an image..."
            accept="image/*"
          />
        </Pane>

        <Pane className="input-row">
          <span>Transmission</span>
          <Select
            value={form.transmission}
            onChange={(e) => setForm({ ...form, transmission: e.target.value })}
          >
            <option value="automatic">Automatic</option>
            <option value="manual">Manual</option>
          </Select>
        </Pane>

        <Pane className="input-row">
          <span>Fuel Type</span>
          <Select
            value={form.fuel}
            onChange={(e) => setForm({ ...form, fuel: e.target.value })}
          >
            <option value="petrol">Petrol</option>
            <option value="diesel">Diesel</option>
            <option value="electric">Electric</option>
            <option value="hybrid">Hybrid</option>
          </Select>
        </Pane>

        <Pane className="input-row">
          <span>Seats</span>
          <TextInput
            value={form.seats}
            onChange={(e) => setForm({ ...form, seats: e.target.value })}
            type="number"
            step="1"
            placeholder="Enter number of seats"
          />
        </Pane>

        <Pane className="input-row">
          <span>Luggage (Bags)</span>
          <TextInput
            value={form.bags}
            onChange={(e) => setForm({ ...form, bags: e.target.value })}
            type="number"
            step="1"
            placeholder="Enter number of bags"
          />
        </Pane>

        <Pane className="input-row">
          <span>Air Conditioning</span>
          <Select
            value={form.air_conditioning.toString()}
            onChange={(e) => setForm({ ...form, air_conditioning: e.target.value === "true" })}
          >
            <option value="true">Yes</option>
            <option value="false">No</option>
          </Select>
        </Pane>

        <Pane className="input-row">
          <span>Airbags</span>
          <TextInput
            value={form.airbags}
            onChange={(e) => setForm({ ...form, airbags: e.target.value })}
            placeholder="e.g., Front & Side"
          />
        </Pane>

        <Pane className="input-row">
          <span>Reverse Camera</span>
          <Select
            value={form.reverse_camera.toString()}
            onChange={(e) => setForm({ ...form, reverse_camera: e.target.value === "true" })}
          >
            <option value="true">Yes</option>
            <option value="false">No</option>
          </Select>
        </Pane>

        <Pane className="input-row">
          <span>Lane Assist</span>
          <Select
            value={form.lane_assist.toString()}
            onChange={(e) => setForm({ ...form, lane_assist: e.target.value === "true" })}
          >
            <option value="true">Yes</option>
            <option value="false">No</option>
          </Select>
        </Pane>

        <Pane className="input-row">
          <span>GPS</span>
          <Select
            value={form.gps.toString()}
            onChange={(e) => setForm({ ...form, gps: e.target.value === "true" })}
          >
            <option value="true">Yes</option>
            <option value="false">No</option>
          </Select>
        </Pane>

        <Pane className="input-row">
          <span>USB</span>
          <Select
            value={form.usb.toString()}
            onChange={(e) => setForm({ ...form, usb: e.target.value === "true" })}
          >
            <option value="true">Yes</option>
            <option value="false">No</option>
          </Select>
        </Pane>

        <Pane className="input-row">
          <span>Bluetooth</span>
          <Select
            value={form.bluetooth.toString()}
            onChange={(e) => setForm({ ...form, bluetooth: e.target.value === "true" })}
          >
            <option value="true">Yes</option>
            <option value="false">No</option>
          </Select>
        </Pane>

        <Pane className="input-row">
          <span>Touchscreen</span>
          <Select
            value={form.touchscreen.toString()}
            onChange={(e) => setForm({ ...form, touchscreen: e.target.value === "true" })}
          >
            <option value="true">Yes</option>
            <option value="false">No</option>
          </Select>
        </Pane>

        <Pane className="input-row">
          <span>Mileage</span>
          <TextInput
            value={form.mileage}
            onChange={(e) => setForm({ ...form, mileage: e.target.value })}
            type="number"
            step="0.1"
            placeholder="e.g., 25"
          />
        </Pane>

        <Button appearance="primary" onClick={handleSubmit}>
          Add Car
        </Button>
      </Pane>
    </Pane>
  );
};

export default AdminCarHire;